import {z} from 'zod'

export const ImagesQuality = z.object({
    month: z.string(),
    monthlyQualityIssuesPercentage: z.number().min(0)
})
export type ImagesQuality = z.infer<typeof ImagesQuality>

export const InsightMetrics = z.object({
    assignmentResponseRate: z.number().min(0),
    setsMeetingMinimumCoveragePercentage: z.number().min(0),
    qualityIssuesPercentage: z.number().min(0),
    assignmentAcceptanceRate: z.number().min(0),
    imageQualityChart: z.array(ImagesQuality)
})
export type InsightMetrics = z.infer<typeof InsightMetrics>

// v3 returns thumbnails as a free-form map of size -> url (e.g. {xs, s, m, l})
export const Thumbnail = z.record(z.string(), z.string()).nullable()
export type Thumbnail = z.infer<typeof Thumbnail>

export const OpportunityImage = z.object({
    id: z.number(),
    photosetId: z.number(),
    photosetSpaceId: z.number().nullable(),
    // Entity title can still be null because the underlying DB column is nullable.
    entityTitle: z.string().nullable(),
    entityExternalId: z.union([z.string(), z.number()]),
    pathLocation: z.string(),
    aiRetouchedPathLocation: z.string().nullable(),
    retouchedPathLocation: z.string().nullable(),
    name: z.string(),
    extension: z.string(),
    shootAt: z.string().nullable(),
    severity: z.string().nullable(),
    isFinalSelect: z.boolean(),
    curationApproved: z.boolean().nullable(),
    thumbnails: Thumbnail,
    aiRetouchedThumbnails: Thumbnail,
    needManualRetouching: z.boolean(),
    isManuallyRetouched: z.boolean(),
    retouchedThumbnails: Thumbnail,
    isHdr: z.boolean(),
    retouchedAt: z.string().nullable(),
    uploadedAt: z.string().nullable(),
    vettingQaApproved: z.boolean(),
    needsToReview: z.boolean(),
    isRevised: z.boolean(),
    revisedPathLocation: z.string().nullable(),
    revisedThumbnails: Thumbnail
})
export type OpportunityImage = z.infer<typeof OpportunityImage>

export const Opportunity = z.object({
    id: z.number(),
    keyName: z.enum([
        'metering_issue',
        'file_format',
        'camera_height_issue',
        'tilted_tripod',
        'wide_tight_distortion',
        'no_lens_hood',
        'dirty_lens_sensor',
        'focus_issues',
        'bracket_merging_issue',
        'closed_shower_curtains',
        'curtains_closed',
        'artificial_lighting',
        'flash',
        'aperture_issue',
        'shutter_speed_issue',
        'high_iso',
        'lens_is_too_tight',
        'lens_is_too_wide',
        'no_tripod_cell_phone',
        'missed_removal'
    ]),
    images: z.array(OpportunityImage)
})
export type Opportunity = z.infer<typeof Opportunity>

export const MissingCoverageFlaggedAssignment = z.object({
    id: z.number(),
    entityTitle: z.string().nullable(),
    toShootAt: z.string().nullable(),
    listingUrl: z.string()
})
export type MissingCoverageFlaggedAssignment = z.infer<typeof MissingCoverageFlaggedAssignment>

export const MissingCoverageSpace = z.object({
    id: z.number(),
    keyName: z.enum([
        'bathroom',
        'bedroom_1',
        'bedroom_2',
        'bedroom_3',
        'bedroom_4',
        'bedroom_5',
        'bedroom_6',
        'bedroom_7',
        'bedroom_8',
        'bedroom_9',
        'bedroom_10',
        'common_spaces',
        'dining_room',
        'exterior',
        'kitchen',
        'living_room',
        'office_study',
        'other',
        'outdoor_space',
        'pool_host_tub'
    ]),
    flaggedIn: z.array(MissingCoverageFlaggedAssignment).nullable()
})
export type MissingCoverageSpace = z.infer<typeof MissingCoverageSpace>

export const CommonIssues = z.object({
    topOpportunities: z.array(Opportunity),
    missingCoverageSpaces: z.array(MissingCoverageSpace)
})
export type CommonIssues = z.infer<typeof CommonIssues>
